import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sprout } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1657626625832-2c0851cdaa9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay: 0.4 + index * 0.16, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const RotatingBadge = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: 1.6, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className="hidden lg:flex absolute bottom-40 right-16 w-32 h-32 items-center justify-center"
  >
    <svg viewBox="0 0 120 120" className="absolute inset-0 w-full h-full animate-spin-slow">
      <defs>
        <path id="badge-circle" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" fill="none" />
      </defs>
      <text className="fill-brand-wheat" style={{ fontSize: "10px", letterSpacing: "2.6px", fontWeight: 600 }}>
        <textPath href="#badge-circle">QUALITY SEEDS • STRONGER POTENTIAL • BETTER FARMING •</textPath>
      </text>
    </svg>
    <span className="w-12 h-12 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center backdrop-blur-sm">
      <Sprout className="w-5 h-5 text-brand-wheat" />
    </span>
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-brand-ink">
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={HERO_IMG} alt="Golden wheat field at golden hour" className="w-full h-full object-cover" fetchPriority="high" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/45 to-brand-ink/30" />
      <div className="absolute inset-0 grain opacity-40" />
      <RotatingBadge />

      <div className="container-site relative z-10 pb-32 md:pb-40 pt-44 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-brand-wheat/35 bg-brand-ink/40 backdrop-blur-sm px-4 py-2 mb-8"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
          <span className="text-brand-wheat text-xs font-semibold tracking-[0.2em] uppercase">Premium Seed Brand · Bihar, India</span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[1.03] text-white text-[clamp(2.7rem,7.2vw,5.4rem)] max-w-4xl">
          <MaskedLine index={0}>Quality Seeds for</MaskedLine>
          <MaskedLine index={1}>
            <span className="font-editorial italic font-semibold text-brand-wheat">Stronger Harvest</span>
          </MaskedLine>
          <MaskedLine index={2}>Potential</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
        >
          Premium maize and wheat seed varieties for farmers and agricultural partners across Bihar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/products"
            data-testid="hero-explore-seeds-cta"
            className="group inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-7 py-4 text-sm font-bold text-brand-deep transition-all hover:bg-brand-wheat hover:shadow-xl hover:shadow-brand-gold/25"
          >
            Explore Our Seeds
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/partner"
            data-testid="hero-become-distributor-cta"
            className="inline-flex items-center gap-2.5 rounded-full border border-white/35 px-7 py-4 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white hover:text-brand-deep"
          >
            Become a Distributor
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 text-xs sm:text-sm text-white/55 tracking-wide"
        >
          Quality-focused seed solutions <span className="text-brand-gold mx-1.5">•</span> Professional branded packaging
          <span className="text-brand-gold mx-1.5">•</span> Built for agricultural partners
        </motion.p>
      </div>
    </section>
  );
}
